// 004 (data-model §2): view models for Profile, children and addresses.
import type { Api } from "@/api/types";

export const MAX_CHILDREN = 10;
export const MAX_ADDRESSES = 10;

export type SchoolSummary = {
  id: string;
  name: string;
  logoUrl: string | null;
  classes: Api["SchoolClass"][];
};

export type LinkedChild = {
  kind: "linked";
  id: string;
  name: string;
  initial: string;
  school: SchoolSummary;
  classLevel: string;
  classLabel: string;
  section: string;
  hasOpenOrder: boolean;
};
export type UnlinkedChild = {
  kind: "unlinked";
  id: string;
  name: string;
  initial: string;
  hasOpenOrder: boolean;
};
export type ChildVM = LinkedChild | UnlinkedChild;

export type AddressVM = {
  id: string;
  title: string;
  oneLine: string;
  isDefault: boolean;
  pin: { latitude: number; longitude: number };
  raw: Api["Address"];
};

const initialOf = (name: string) => name.trim().charAt(0).toUpperCase();

/** "CLASS_5" → "Class 5", "LKG" → "LKG", "NURSERY" → "Nursery". */
export function standardClassName(code: string): string {
  const m = /^CLASS_(\d{1,2})$/.exec(code);
  if (m) return `Class ${m[1]}`;
  if (code === "NURSERY") return "Nursery";
  return code;
}

export function classLabel(
  code: string,
  classes: readonly Api["SchoolClass"][],
): string {
  return (
    classes.find((c) => c.classLevel === code)?.name ?? standardClassName(code)
  );
}

export function childVM(s: Api["Student"]): ChildVM {
  if (
    !s.linked ||
    s.school === null ||
    s.classLevel === null ||
    s.section === null
  ) {
    return {
      kind: "unlinked",
      id: s.id,
      name: s.name,
      initial: initialOf(s.name),
      hasOpenOrder: s.hasOpenOrder,
    };
  }
  return {
    kind: "linked",
    id: s.id,
    name: s.name,
    initial: initialOf(s.name),
    school: {
      id: s.school.id,
      name: s.school.name,
      logoUrl: s.school.logoUrl,
      classes: s.school.classes,
    },
    classLevel: s.classLevel,
    classLabel: classLabel(s.classLevel, s.school.classes),
    section: s.section,
    hasOpenOrder: s.hasOpenOrder,
  };
}

const LABELS: Record<Api["AddressLabel"], string> = {
  HOME: "Home",
  SCHOOL: "School",
  OTHER: "Other",
};

/** A label as an order or a school's view sends it: a label code (shown in words), or custom text. */
export function addressLabelText(label: string): string {
  return LABELS[label as Api["AddressLabel"]] ?? label;
}

export function addressTitle(
  a: Pick<Api["Address"], "label" | "customLabel">,
): string {
  return a.label === "OTHER" && a.customLabel ? a.customLabel : LABELS[a.label];
}

export function addressVM(a: Api["Address"]): AddressVM {
  const parts = [
    a.flatHouse,
    a.streetArea,
    ...(a.landmark ? [a.landmark] : []),
  ];
  return {
    id: a.id,
    title: addressTitle(a),
    oneLine: `${parts.join(", ")}, ${a.city} ${a.pincode}`,
    isDefault: a.isDefault,
    pin: { latitude: a.latitude, longitude: a.longitude },
    raw: a,
  };
}

export type ProfileVM = {
  name: string;
  email: string;
  mobileDisplay: string | null;
  initials: string;
  pinSaved: boolean;
};

/** "+919848022338" → "+91 98480 22338". */
export function mobileDisplay(mobile: string | null): string | null {
  if (!mobile) return null;
  const m = /^\+91(\d{5})(\d{5})$/.exec(mobile);
  return m ? `+91 ${m[1]} ${m[2]}` : mobile;
}

export function profileVM(p: Api["Profile"]): ProfileVM {
  const words = p.name.trim().split(/\s+/);
  const initials = (
    (words[0]?.charAt(0) ?? "") +
    (words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? "") : "")
  ).toUpperCase();
  return {
    name: p.name,
    email: p.email,
    mobileDisplay: mobileDisplay(p.mobile),
    initials,
    pinSaved: p.pinSaved,
  };
}

export const canAddChild = (count: number) => count < MAX_CHILDREN;
export const canAddAddress = (count: number) => count < MAX_ADDRESSES;
